import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { SELECT_STYLES, mergeSelectStyles } from "../../utils/selectStyles";
import { CButton, CCollapse, CFormInput } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import * as Sentry from "@sentry/react";
import DashboardContent from "../../components/DashboardContent";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMISSIONS } from "../../utils/permissions";
import RedirectHandler from "../../components/RedirectHandler";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import QuestionItemInfo from "../../components/QuestionItemInfo";
import { ReactComponent as BinIcon } from "../../assets/images/icons/bin.svg";
import { ReactComponent as PlusIcon } from "../../assets/images/icons/plus.svg";
import { ReactComponent as CopyIcon } from "../../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../../assets/images/icons/copy.svg";
import { SCS_GROUPING_TABLE_OPTIONS } from "../../utils/scsDescriptionOfStudiesOptions";
import {
  SCS_SD_OTHER_STUDIES_OPTIONS,
  SCS_SD_QUESTIONS,
} from "../../utils/scsSubjectDispositionQuestions";
import { useScsTableOptions } from "../../hooks/useScsTableOptions";

const isProduction = process.env.NODE_ENV === "production";

const FORM_ID = "scs-subject-disposition-form";
const REMOVE_TOOLTIP_ID = "scs-sd-remove";
const DUPLICATE_TOOLTIP_ID = "scs-sd-duplicate";

// Repeatable study blocks always keep at least one entry; the first entry is
// non-removable (mirrors the Description of Studies / CSR block pattern).
const MIN_BLOCK_ENTRIES = 1;

const emptyStudy = () => ({
  qDispositionGroupingName: "",
  qDispositionTable: "",
  qDispositionTreatments: [""],
});

// Deep-clone a study block so a duplicated block is fully independent from its
// source (mutating one never affects the other).
const cloneStudy = study => ({
  qDispositionGroupingName: study?.qDispositionGroupingName || "",
  qDispositionTable: study?.qDispositionTable || "",
  qDispositionTreatments: [...(study?.qDispositionTreatments || [""])],
});

// Restore tolerance: earlier payloads carried the grouping name under
// `qDispositionGrouping`, or under the question-text attribute
// (`qDispositionOtherStudies`), rather than its own key.
const normalizeStudy = study => ({
  qDispositionGroupingName:
    study?.qDispositionGroupingName ||
    study?.qDispositionGrouping ||
    (typeof study?.qDispositionOtherStudies === "string"
      ? study.qDispositionOtherStudies
      : ""),
  qDispositionTable: study?.qDispositionTable || "",
  qDispositionTreatments:
    Array.isArray(study?.qDispositionTreatments) &&
    study.qDispositionTreatments.length > 0
      ? study.qDispositionTreatments
      : [""],
});

const toOptions = values => (values || []).map(v => ({ value: v, label: v }));

// Render a single-select's chosen value as a chip/tag so it matches the styling
// used by the Description of Studies dropdowns.
const SINGLE_VALUE_CHIP_STYLES = mergeSelectStyles(SELECT_STYLES, {
  singleValue: provided => ({
    ...provided,
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "calc(100% - 8px)",
    backgroundColor: "#eef1f5",
    borderRadius: "4px",
    padding: "3px 6px",
    margin: "2px",
    fontSize: "13px",
    boxSizing: "border-box",
  }),
});

const SCSSubjectDisposition = () => {
  const { studyId: summaryId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // scs:wizards:read gates the GET that pre-populates this page; writing it
  // back needs scs:wizards:update on top, so without either the Save button is
  // disabled and Next navigates without submitting.
  const canReadWizards = hasPermission(PERMISSIONS.scsWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.scsWizardsUpdate);

  // The table dropdown (Q3) is populated from the safety-summary TFL content in
  // the scsData store once the uploaded files have been PROCESSED; until then
  // fall back to the bundled placeholder options.
  const { tableNames: scsTableNames } = useScsTableOptions();
  const tableOptions = scsTableNames.length
    ? scsTableNames
    : SCS_GROUPING_TABLE_OPTIONS;

  const [questionText, setQuestionText] = useState({});
  // Q1 answer: null until the user picks Yes / No.
  const [otherStudies, setOtherStudies] = useState(null);
  const [studies, setStudies] = useState([emptyStudy()]);
  // Which study accordion items are open (by array index). Only relevant when
  // there is more than one study; the newly added / duplicated block (always
  // the last) is the one left expanded.
  const [openStudyIndexes, setOpenStudyIndexes] = useState(() => new Set([0]));
  const lastStudyRef = useRef(null);
  const shouldScrollStudyRef = useRef(false);

  const [otherStudiesError, setOtherStudiesError] = useState("");
  const [studyErrors, setStudyErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  const studyRefs = useRef({});
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Question wording is served from the shared safety-summary questions
  // endpoint; fall back to the bundled constants when it is unavailable.
  useEffect(() => {
    CsrAPI.getSafetySummaryQuestions()
      .then(data => {
        if (mountedRef.current && data) setQuestionText(data);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Pre-populate previously saved wizard state.
  useEffect(() => {
    if (!summaryId) return;
    // Without scs:wizards:read the endpoint is never called; the page starts
    // from its empty form instead.
    if (!canReadWizards) return;

    setAjaxLoading(true);
    CsrAPI.getSafetySummarySubjectDisposition({ summaryId })
      .then(data => {
        if (!mountedRef.current) return;
        const disposition = data?.subject_disposition || data || {};

        const saved = disposition.qDispositionOtherStudies;
        if (typeof saved === "boolean") {
          setOtherStudies(saved);
        } else if (saved === "Yes" || saved === "No") {
          setOtherStudies(saved === "Yes");
        }

        // `studies` is the pre-rename array key, kept for restore tolerance.
        const savedStudies = Array.isArray(
          disposition.qDispositionGroupingNames
        )
          ? disposition.qDispositionGroupingNames
          : disposition.studies;

        if (Array.isArray(savedStudies) && savedStudies.length) {
          const restored = savedStudies.map(normalizeStudy);
          setStudies(restored);
          // Keep only the last study expanded once saved data is restored.
          setOpenStudyIndexes(new Set([restored.length - 1]));
        }
      })
      .catch(error => {
        if (!mountedRef.current) return;
        // A 404 simply means nothing has been saved yet — start with defaults.
        if (error?.response?.status === 404) return;
        if (isProduction) Sentry.captureException(error);
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to load previously saved data.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setAjaxLoading(false);
      });
  }, [summaryId, setAjaxLoading, canReadWizards]);

  // Scroll the newly added / duplicated study into view. Walk up to the named
  // scroll container so we never scroll the browser viewport.
  useEffect(() => {
    if (!shouldScrollStudyRef.current || !lastStudyRef.current) return;
    const el = lastStudyRef.current;
    let wrapper = el.parentElement;
    while (
      wrapper &&
      !wrapper.classList.contains("dashboard-content__wrapper")
    ) {
      wrapper = wrapper.parentElement;
    }
    if (wrapper) {
      const elTop = el.getBoundingClientRect().top;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
    }
    shouldScrollStudyRef.current = false;
  }, [studies.length]);

  // --- Study blocks -------------------------------------------------------
  // "Add another study" appends an empty block; "Duplicate study above"
  // appends a copy of the last block; the per-block "Duplicate" pill appends a
  // copy of that block. New blocks always go to the end and become the only
  // expanded accordion item.
  const appendStudy = block =>
    setStudies(prev => {
      const next = [...prev, block];
      shouldScrollStudyRef.current = true;
      setOpenStudyIndexes(new Set([next.length - 1]));
      return next;
    });
  const addStudy = () => appendStudy(emptyStudy());
  const duplicateStudy = index =>
    setStudies(prev => {
      if (!prev[index]) return prev;
      const next = [...prev, cloneStudy(prev[index])];
      shouldScrollStudyRef.current = true;
      setOpenStudyIndexes(new Set([next.length - 1]));
      return next;
    });
  const duplicateStudyAbove = () => {
    if (!studies.length) return;
    duplicateStudy(studies.length - 1);
  };
  const removeStudy = index =>
    setStudies(prev => {
      if (prev.length <= MIN_BLOCK_ENTRIES) return prev;
      const next = prev.filter((_, i) => i !== index);
      // Leave the last remaining study expanded.
      setOpenStudyIndexes(new Set([next.length - 1]));
      return next;
    });
  const toggleStudy = index =>
    setOpenStudyIndexes(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  const updateStudy = (index, updater) =>
    setStudies(prev =>
      prev.map((row, i) => (i === index ? updater(row) : row))
    );

  // Treatment names (Q4) are a repeatable list; the first input is
  // non-removable.
  const baseTreatments = row =>
    (row.qDispositionTreatments || []).length > 0
      ? row.qDispositionTreatments
      : [""];

  const validate = () => {
    if (otherStudies === null) {
      setOtherStudiesError("Please select an answer");
      setStudyErrors({});
      return false;
    }
    setOtherStudiesError("");

    // Answering "No" ends the section — nothing else to validate.
    if (!otherStudies) {
      setStudyErrors({});
      return true;
    }

    const errors = {};
    studies.forEach((row, index) => {
      if (!row.qDispositionGroupingName?.trim()) {
        errors[index] = {
          qDispositionGroupingName: "Grouping name is required",
        };
      }
    });
    setStudyErrors(errors);

    const firstInvalid = Object.keys(errors)[0];
    if (firstInvalid !== undefined) {
      const index = Number(firstInvalid);
      // Expand the offending block before focusing it, otherwise the field is
      // hidden inside a collapsed accordion item.
      setOpenStudyIndexes(prev => new Set(prev).add(index));
      studyRefs.current[`${index}-qDispositionGroupingName`]?.focus();
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    qDispositionOtherStudies: Boolean(otherStudies),
    // "No" ends the section, so no study blocks are reported.
    qDispositionGroupingNames: otherStudies
      ? studies.map(row => ({
          qDispositionGroupingName: row.qDispositionGroupingName,
          qDispositionTable: row.qDispositionTable || null,
          qDispositionTreatments: baseTreatments(row).filter(treatment =>
            treatment.trim()
          ),
        }))
      : [],
  });

  // Persists the wizard state via PATCH .../wizards/subject_disposition.
  // Shared by the "Save" button (stays on the page) and the "Overall Extent of
  // Exposure" button (navigates on success); returns whether the save succeeded
  // so the caller can decide what to do next.
  const saveWizardState = async () => {
    if (isSubmitting) return false;
    if (!canSaveWizards) return false;
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return false;
    }

    setIsSubmitting(true);
    try {
      await CsrAPI.saveSafetySummarySubjectDisposition({
        summaryId,
        state: buildPayload(),
      });
      return true;
    } catch (error) {
      if (isProduction) Sentry.captureException(error);
      if (mountedRef.current) {
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to save. Please try again.");
        }
      }
      return false;
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!canSaveWizards) {
      navigate(`/scs/${summaryId}/overall-extent-of-exposure`);
      return;
    }

    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      navigate(`/scs/${summaryId}/overall-extent-of-exposure`);
    }
  };

  const handleSave = async event => {
    event?.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      toast.success("Data saved successfully!");
    }
  };

  const removeButton = (label, onClick) => (
    <CButton
      color={"danger"}
      shape={"pill"}
      aria-label={label}
      data-tooltip-id={REMOVE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <BinIcon />
    </CButton>
  );

  const addButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <PlusIcon />
    </CButton>
  );

  // Solid-blue pill sitting at the top of each study block.
  const duplicateBlockButton = onClick => (
    <CButton
      color={"primary"}
      shape={"pill"}
      className={"scs-dos__duplicate"}
      data-tooltip-id={DUPLICATE_TOOLTIP_ID}
      data-tooltip-content={"Duplicate this study"}
      onClick={onClick}>
      <CopyIcon /> Duplicate
    </CButton>
  );

  // Outlined pill pairing with "Add another study" at the bottom of the page.
  const duplicateAboveButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <CopyOutlineIcon />
    </CButton>
  );

  // Endpoint wording wins; fall back to the bundled question text.
  const qText = key => {
    const question = SCS_SD_QUESTIONS[key];
    return (
      questionText[question?.textKey || key] ||
      questionText[key] ||
      question?.text ||
      ""
    );
  };

  // CSR-style numbered question: index badge in the left gutter, label +
  // control in the item column (mirrors QuestionItem).
  const renderQuestion = (key, control, numberOverride) => {
    const question = SCS_SD_QUESTIONS[key];
    if (!question) return null;
    return (
      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>
            {numberOverride ?? question.number}
          </span>
        </div>
        <div className={"questions-content__item"}>
          <div className={"questions-content__label"}>
            <span className={"questions-content__label-text"}>
              {qText(key)}
              {question.required && (
                <span className={"questions-content__required-asterisk"}>
                  {" "}
                  *
                </span>
              )}
            </span>
            {question.tooltip && (
              <QuestionItemInfo
                questionName={key}
                notes={{ [key]: question.tooltip }}
              />
            )}
          </div>
          <div className={"questions-content__input"}>{control}</div>
        </div>
      </div>
    );
  };

  // Q1 — Yes / No toggle. Selecting "No" ends the section.
  const renderOtherStudies = () =>
    renderQuestion(
      "qDispositionOtherStudies",
      <>
        {SCS_SD_OTHER_STUDIES_OPTIONS.map(option => (
          <React.Fragment key={String(option.value)}>
            <input
              type={"radio"}
              className={"btn-check"}
              name={"disposition-other-studies"}
              id={`disposition-other-studies-${option.label}`}
              autoComplete={"off"}
              checked={otherStudies === option.value}
              onChange={() => {
                setOtherStudies(option.value);
                setOtherStudiesError("");
              }}
            />
            <label
              className={"btn btn-light"}
              htmlFor={`disposition-other-studies-${option.label}`}>
              {option.label}
            </label>
          </React.Fragment>
        ))}
        {otherStudiesError && (
          <span className={"errmsg"}>{otherStudiesError}</span>
        )}
      </>
    );

  const renderTreatments = (study, index, numberOverride) =>
    renderQuestion(
      "qDispositionTreatments",
      <>
        {baseTreatments(study).map((treatment, treatmentIndex) => (
          <div
            key={`treatment-${index}-${treatmentIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={treatment}
                placeholder={"Treatment name"}
                onChange={e =>
                  updateStudy(index, row => ({
                    ...row,
                    qDispositionTreatments: baseTreatments(row).map((t, i) =>
                      i === treatmentIndex ? e.target.value : t
                    ),
                  }))
                }
              />
            </div>
            {treatmentIndex === 0 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove treatment", () =>
                updateStudy(index, row => ({
                  ...row,
                  qDispositionTreatments: baseTreatments(row).filter(
                    (_, i) => i !== treatmentIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add more", () =>
          updateStudy(index, row => ({
            ...row,
            qDispositionTreatments: [...baseTreatments(row), ""],
          }))
        )}
      </>,
      numberOverride
    );

  // A single study block (questions 2–4). Rendered on its own when there is one
  // study, or inside an accordion item when there are many.
  const renderStudyBlock = (study, index) => {
    const isFirstBlock = index === 0;
    const blockNumber = index + 1;
    // First block keeps the plain page numbers (2..4); every additional block
    // appends "(blockNumber)" to each question index (e.g. 2(2), 3(2), 4(2)).
    const q = n => (isFirstBlock ? n : `${n}(${blockNumber})`);
    const rowErrors = studyErrors[index] || {};
    return (
      <>
        <div className={"scs-dos__block-actions"}>
          {duplicateBlockButton(() => duplicateStudy(index))}
          {index > 0 && removeButton("Remove study", () => removeStudy(index))}
        </div>
        {renderQuestion(
          "qDispositionGroupingName",
          <div className={"form-component"}>
            <CFormInput
              ref={el =>
                (studyRefs.current[`${index}-qDispositionGroupingName`] = el)
              }
              value={study.qDispositionGroupingName || ""}
              placeholder={"Grouping name"}
              invalid={Boolean(rowErrors.qDispositionGroupingName)}
              onChange={e =>
                updateStudy(index, row => ({
                  ...row,
                  qDispositionGroupingName: e.target.value,
                }))
              }
            />
            {rowErrors.qDispositionGroupingName && (
              <span className={"errmsg"}>
                {rowErrors.qDispositionGroupingName}
              </span>
            )}
          </div>,
          q(2)
        )}
        {renderQuestion(
          "qDispositionTable",
          <Select
            className={"scs-dos__control"}
            styles={SINGLE_VALUE_CHIP_STYLES}
            options={toOptions(tableOptions)}
            value={
              study.qDispositionTable
                ? {
                    value: study.qDispositionTable,
                    label: study.qDispositionTable,
                  }
                : null
            }
            onChange={selected =>
              updateStudy(index, row => ({
                ...row,
                qDispositionTable: selected ? selected.value : "",
              }))
            }
            placeholder={"Select table"}
            isClearable
          />,
          q(3)
        )}
        {renderTreatments(study, index, q(4))}
      </>
    );
  };

  const renderStudies = () => (
    <>
      {studies.length === 1 ? (
        <div ref={lastStudyRef} className={"analysis-block"}>
          {renderStudyBlock(studies[0], 0)}
        </div>
      ) : (
        <>
          <div className={"scs-dos__accordion-controls"}>
            <CButton
              color={"primary"}
              shape={"pill"}
              className={"scs-dos__duplicate"}
              onClick={() =>
                setOpenStudyIndexes(new Set(studies.map((_, i) => i)))
              }>
              Expand all
            </CButton>
            <CButton
              color={"primary"}
              shape={"pill"}
              className={"scs-dos__duplicate"}
              onClick={() => setOpenStudyIndexes(new Set())}>
              Collapse all
            </CButton>
          </div>
          <div className={"accordion"}>
            {studies.map((study, index) => {
              const isOpen = openStudyIndexes.has(index);
              const isLast = index === studies.length - 1;
              return (
                <div
                  key={`study-${index}`}
                  className={"accordion-item"}
                  ref={isLast ? lastStudyRef : null}>
                  <div className={"accordion-header"}>
                    <button
                      type={"button"}
                      className={`accordion-button${isOpen ? "" : " collapsed"}`}
                      onClick={() => toggleStudy(index)}>
                      Study {index + 1}
                    </button>
                  </div>
                  <CCollapse visible={isOpen} className={"accordion-collapse"}>
                    <div className={"accordion-body"}>
                      <div className={"analysis-block"}>
                        {renderStudyBlock(study, index)}
                      </div>
                    </div>
                  </CCollapse>
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className={"scs-dos__block-footer"}>
        {addButton("Add another study", addStudy)}
        {duplicateAboveButton("Duplicate study above", duplicateStudyAbove)}
      </div>
    </>
  );

  // Reuses the `.scs-dos` styles — the block / accordion / duplicate controls
  // are identical to the Description of Studies wizard.
  const PageContent = () => (
    <div className={"questions-page scs-dos"}>
      <div className={"questions-content"}>
        <div className={"questions-content__items"}>
          <ToastContainer />
          <form id={FORM_ID} onSubmit={handleSubmit}>
            {renderOtherStudies()}
            {otherStudies === true && renderStudies()}
          </form>
          <Tooltip
            id={REMOVE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
          <Tooltip
            id={DUPLICATE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
        </div>
      </div>
    </div>
  );

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Subject Disposition"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Description of Studies",
        buttonGoToAction: `/scs/${summaryId}/description-of-studies`,
      }}
      saveButton={true}
      savePermission={[
        PERMISSIONS.scsWizardsRead,
        PERMISSIONS.scsWizardsUpdate,
      ]}
      saveButtonHandler={handleSave}
      saveButtonDisabled={isSubmitting}
      nextButton={{
        buttonTitle: isSubmitting ? "Saving..." : "Overall Extent of Exposure",
        buttonFormAction: FORM_ID,
        buttonGoToAction: `/scs/${summaryId}/overall-extent-of-exposure`,
        disabled: isSubmitting,
      }}
    />
  );
};

export default SCSSubjectDisposition;
